import { useMemo } from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import IconButton from "@mui/material/IconButton";
import InputBase from "@mui/material/InputBase";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { BdcStockDay, BdcStockDayDc, BdcStockDayItem } from "../../../types/bdc.type";
import { baht, pcs, thaiDate } from "../format";
import KpiRow from "../KpiRow";
import { CARD_SX } from "../styles";
import Tag from "../Tag";
import {
  DcField,
  DraftErrors,
  ItemField,
  StockDayDraft,
  dcText,
  isDcOverridden,
  itemText,
  pctText,
  percentOf,
  previewDay,
  systemDcValue,
} from "./calc";

/** Cells the system fills (grey) vs those the GM types (white) — the board's legend. */
const AUTO_BG = "#F4F5F7";
const OVERRIDE = "#FDA42C";
const ERROR = "#C62828";

export const STOCK_TABLE_SX = {
  "& .bdc-t td.auto": { background: AUTO_BG, color: "#3A3E48" },
  "& .bdc-t td.ed": { padding: "4px 6px" },
  "& .bdc-t .short": { color: ERROR },
} as const;

function QtyInput({
  value,
  onChange,
  label,
  placeholder,
  error,
  auto,
  overridden,
  width = 76,
  align = "right",
}: Readonly<{
  value: string;
  onChange: (text: string) => void;
  label: string;
  placeholder?: string;
  error?: string;
  /** Blank shows the system's figure as the placeholder, on the grey of a system cell. */
  auto?: boolean;
  overridden?: boolean;
  width?: number;
  align?: "right" | "left";
}>) {
  let border = "#C9CBD2";
  if (overridden) border = OVERRIDE;
  if (error) border = ERROR;
  const input = (
    <InputBase
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      error={Boolean(error)}
      inputProps={{
        "aria-label": label,
        "aria-invalid": Boolean(error),
        inputMode: align === "right" ? "numeric" : "text",
      }}
      sx={{
        width,
        fontSize: "0.8125rem",
        border: `1px solid ${border}`,
        borderRadius: "5px",
        bgcolor: auto && !value ? AUTO_BG : "#FFFFFF",
        "& input": { padding: "3px 6px", textAlign: align, fontVariantNumeric: "tabular-nums" },
        "& input::placeholder": { color: auto ? "#3A3E48" : "#9A9EA8", opacity: 1 },
        "&.Mui-focused": { borderColor: error ? ERROR : "#363A44" },
      }}
    />
  );
  // always inside the Tooltip (an empty title shows nothing) so an error never remounts the input
  return (
    <Tooltip title={error ?? ""} arrow>
      {input}
    </Tooltip>
  );
}

/** A system figure the GM may override: blank = the system's; a reset puts it back. */
function OverridableCell({
  text,
  systemValue,
  overridden,
  label,
  error,
  onChange,
}: Readonly<{
  text: string;
  systemValue: number;
  overridden: boolean;
  label: string;
  error?: string;
  onChange: (text: string) => void;
}>) {
  return (
    <Stack direction="row" alignItems="center" justifyContent="flex-end" gap={0.25}>
      {overridden && (
        <Tooltip title={`แก้จากค่าระบบ ${pcs(systemValue)} — กดเพื่อกลับไปใช้ค่าระบบ`}>
          <IconButton size="small" aria-label={`ใช้ค่าระบบ ${label}`} onClick={() => onChange("")} sx={{ p: 0.25 }}>
            <RestartAltIcon sx={{ fontSize: 16, color: OVERRIDE }} />
          </IconButton>
        </Tooltip>
      )}
      <QtyInput
        value={text}
        onChange={onChange}
        label={label}
        placeholder={pcs(systemValue)}
        error={error}
        auto
        overridden={overridden}
      />
    </Stack>
  );
}

/** A read-only overridden figure: the value with an amber mark saying what the system had. */
function OverriddenValue({ value, systemValue, overridden }: Readonly<{ value: number; systemValue: number; overridden: boolean }>) {
  if (!overridden) return <>{pcs(value)}</>;
  return (
    <Tooltip title={`แก้จากค่าระบบ ${pcs(systemValue)}`}>
      <span style={{ borderBottom: `2px solid ${OVERRIDE}` }}>{pcs(value)}</span>
    </Tooltip>
  );
}

const countText = (value: number | null) => (value === null ? "-" : pcs(value));

function OverShort({ item }: Readonly<{ item: Pick<BdcStockDayItem, "over" | "short"> }>) {
  if (item.short > 0) return <b className="short">ขาด {pcs(item.short)}</b>;
  return <b>{pcs(item.over)}</b>;
}

function ItemRow({
  source,
  view,
  draft,
  errors,
  editable,
  onChange,
}: Readonly<{
  source: BdcStockDayItem;
  view: BdcStockDayItem;
  draft: StockDayDraft;
  errors: DraftErrors;
  editable: boolean;
  onChange: (barcode: string, field: ItemField, text: string) => void;
}>) {
  const own = draft.items[source.barcode];
  const err = errors.items[source.barcode] ?? {};
  const text = (field: ItemField) => itemText(source, own, field);
  const set = (field: ItemField) => (value: string) => onChange(source.barcode, field, value);
  const name = source.name;

  const qty = (field: ItemField, label: string, placeholder?: string) => (
    <QtyInput value={text(field)} onChange={set(field)} label={`${label} ${name}`} placeholder={placeholder} error={err[field]} />
  );

  const transfer = (qtyField: "transfer_in" | "transfer_out", noteField: "transfer_in_note" | "transfer_out_note", label: string) => {
    if (!editable) {
      return (
        <>
          {pcs(view[qtyField])}
          {view[noteField] && <div className="sub">{view[noteField]}</div>}
        </>
      );
    }
    const showNote = view[qtyField] > 0 || text(noteField) !== "" || Boolean(err[noteField]);
    return (
      <Stack gap={0.5} alignItems="flex-end">
        {qty(qtyField, label, "0")}
        {showNote && (
          <QtyInput
            value={text(noteField)}
            onChange={set(noteField)}
            label={`หมายเหตุ${label} ${name}`}
            placeholder="หมายเหตุ"
            error={err[noteField]}
            width={150}
            align="left"
          />
        )}
      </Stack>
    );
  };

  return (
    <tr>
      <td>
        <Stack direction="row" gap={0.75} alignItems="center">
          <span>{name}</span>
          {view.is_count_mismatch && (
            <Tooltip
              title={`เหลือ + ชำรุด + ขาย = ${pcs((view.leftover ?? 0) + (view.damaged ?? 0) + (view.sold ?? 0))} ไม่เท่ากับเกิน ${pcs(view.over)}`}
            >
              <span>
                <Tag tone="warn">นับไม่ตรง</Tag>
              </span>
            </Tooltip>
          )}
        </Stack>
        <span className="sub">
          {source.item_code}
          {source.size ? ` · ${source.size}` : ""}
        </span>
      </td>
      <td className={`n ${editable ? "ed" : "auto"}`}>
        {editable ? (
          <OverridableCell
            text={text("carry_in")}
            systemValue={source.carry_in_system}
            overridden={view.is_carry_in_overridden}
            label={`ยกมา ${name}`}
            error={err.carry_in}
            onChange={set("carry_in")}
          />
        ) : (
          <OverriddenValue value={view.carry_in} systemValue={source.carry_in_system} overridden={view.is_carry_in_overridden} />
        )}
      </td>
      <td className="n ed">{editable ? qty("factory_in", "โรงงาน", "0") : pcs(view.factory_in)}</td>
      <td className="n auto">{pcs(view.total_in)}</td>
      <td className="n auto">{pcs(view.system_out)}</td>
      <td className="n ed">{transfer("transfer_in", "transfer_in_note", "รับโอน")}</td>
      <td className="n ed">{transfer("transfer_out", "transfer_out_note", "โอนออก")}</td>
      <td className="n auto">
        <OverShort item={view} />
      </td>
      <td className="n ed">{editable ? qty("leftover", "เหลือ", pcs(view.over)) : countText(view.leftover)}</td>
      <td className="n ed">{editable ? qty("damaged", "ชำรุด", "0") : countText(view.damaged)}</td>
      <td className="n ed">{editable ? qty("sold", "ขาย", "0") : countText(view.sold)}</td>
    </tr>
  );
}

function DcRow({
  source,
  view,
  draft,
  errors,
  editable,
  onChange,
}: Readonly<{
  source: BdcStockDayDc;
  view: BdcStockDayDc;
  draft: StockDayDraft;
  errors: DraftErrors;
  editable: boolean;
  onChange: (dcCode: string, field: DcField, text: string) => void;
}>) {
  const own = draft.dcs[source.dc_code];
  const err = errors.dcs[source.dc_code] ?? {};
  const cell = (field: DcField, label: string) => {
    const system = systemDcValue(source, field);
    const overridden = isDcOverridden(view, field);
    return editable ? (
      <OverridableCell
        text={dcText(source, own, field)}
        systemValue={system}
        overridden={overridden}
        label={`${label} ${source.dc_code}`}
        error={err[field]}
        onChange={(value) => onChange(source.dc_code, field, value)}
      />
    ) : (
      <OverriddenValue value={view[field]} systemValue={system} overridden={overridden} />
    );
  };
  return (
    <tr>
      <td>
        <span className="dc">{source.dc_code}</span> {source.dc_name}
      </td>
      <td className="n auto">{pcs(view.ordered_qty)}</td>
      <td className={`n ${editable ? "ed" : "auto"}`}>{cell("store_total", "ร้านทั้งหมด")}</td>
      <td className={`n ${editable ? "ed" : "auto"}`}>{cell("store_ordered", "ร้านที่สั่งวันนี้")}</td>
      <td className="n auto">{pctText(view.ordered_store_pct)}</td>
    </tr>
  );
}

function Legend() {
  const swatch = (bg: string, border: string) => (
    <Box component="span" sx={{ width: 12, height: 12, borderRadius: "2px", bgcolor: bg, border: `1px solid ${border}` }} />
  );
  return (
    <Stack direction="row" gap={1.5} alignItems="center" sx={{ fontSize: "0.75rem", color: "#3A3E48" }}>
      <Stack direction="row" gap={0.6} alignItems="center">
        {swatch(AUTO_BG, "#C9CBD2")}ระบบเติม
      </Stack>
      <Stack direction="row" gap={0.6} alignItems="center">
        {swatch("#FFFFFF", "#9A9EA8")}GM กรอก
      </Stack>
      <Stack direction="row" gap={0.6} alignItems="center">
        {swatch("#FFFFFF", OVERRIDE)}แก้จากค่าระบบ
      </Stack>
    </Stack>
  );
}

const SECTION_SX = { ...CARD_SX, p: 1.75, display: "flex", flexDirection: "column", gap: 1 } as const;

/**
 * ขาด-เกิน of one day (board StockDay): KPI tiles, the per-SKU grid, the per-DC block and
 * แคปให้บัญชี. Editable cells only when `editable` (หัวหน้า BDC / MASTER on an open day); the
 * figures shown are the draft laid over the server's day, recomputed as the server would.
 */
export default function DayView({
  day,
  draft,
  errors,
  editable,
  onItemChange,
  onDcChange,
}: Readonly<{
  day: BdcStockDay;
  draft: StockDayDraft;
  errors: DraftErrors;
  editable: boolean;
  onItemChange: (barcode: string, field: ItemField, text: string) => void;
  onDcChange: (dcCode: string, field: DcField, text: string) => void;
}>) {
  const view = useMemo(() => previewDay(day, draft), [day, draft]);
  const { totals, accounting } = view;
  const shortSkus = view.items.filter((item) => item.short > 0).length;
  const overriddenCarry = view.items.filter((item) => item.is_carry_in_overridden).length;
  const orderingDcs = view.dcs.filter((dc) => dc.ordered_qty > 0).length;
  const carrySub = day.carry_from_date ? `"เหลือ" ของ ${thaiDate(day.carry_from_date)}` : "ไม่มีวันก่อนหน้าที่ปิดแล้ว";

  return (
    <Stack gap={1.75}>
      <KpiRow
        items={[
          {
            label: "ยกมา",
            value: pcs(totals.carry_in),
            sub: overriddenCarry > 0 ? `${carrySub} · แก้ ${overriddenCarry} รายการ` : carrySub,
          },
          { label: "รับจากโรงงาน", value: pcs(totals.factory_in), sub: "GM กรอก" },
          {
            label: "7-11 สั่ง (ระบบ)",
            value: pcs(totals.system_out),
            sub: `จากไฟล์ ${orderingDcs} DC · ${pcs(totals.store_ordered)} ร้าน`,
          },
          { label: "เกิน", value: pcs(totals.over), sub: `${pctText(totals.over_pct)} ของของที่มี`, tone: "warn" },
          {
            label: "ขาด",
            value: pcs(totals.short),
            sub: shortSkus > 0 ? `${shortSkus} สินค้าของไม่พอ` : "ไม่มีสินค้าที่ของไม่พอ",
          },
        ]}
      />

      <Card sx={SECTION_SX}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
          <Typography fontWeight={600}>ต่อสินค้า</Typography>
          <Legend />
        </Stack>
        <Box sx={{ overflowX: "auto" }}>
          <table className="bdc-t">
            <thead>
              <tr>
                <th>สินค้า</th>
                <th className="n">ยกมา</th>
                <th className="n">โรงงาน</th>
                <th className="n">รวม</th>
                <th className="n">7-11 สั่ง</th>
                <th className="n">รับโอน</th>
                <th className="n">โอนออก</th>
                <th className="n">เกิน / ขาด</th>
                <th className="n">เหลือ</th>
                <th className="n">ชำรุด</th>
                <th className="n">ขาย</th>
              </tr>
            </thead>
            <tbody>
              {day.items.length === 0 ? (
                <tr>
                  <td colSpan={11} className="c sub">
                    ไม่มีสินค้าในวันนี้ — ยังไม่มีไฟล์ ORDS ของวันนี้ และไม่มีของยกมา
                  </td>
                </tr>
              ) : (
                day.items.map((item, index) => (
                  <ItemRow
                    key={item.barcode}
                    source={item}
                    view={view.items[index]}
                    draft={draft}
                    errors={errors}
                    editable={editable}
                    onChange={onItemChange}
                  />
                ))
              )}
            </tbody>
            {day.items.length > 0 && (
              <tfoot>
                <tr>
                  <td>รวม</td>
                  <td className="n">{pcs(totals.carry_in)}</td>
                  <td className="n">{pcs(totals.factory_in)}</td>
                  <td className="n">{pcs(totals.total_in)}</td>
                  <td className="n">{pcs(totals.system_out)}</td>
                  <td className="n">{pcs(totals.transfer_in)}</td>
                  <td className="n">{pcs(totals.transfer_out)}</td>
                  <td className="n">
                    {pcs(totals.over)}
                    {totals.short > 0 && <div className="short">ขาด {pcs(totals.short)}</div>}
                  </td>
                  <td className="n">{pcs(totals.leftover)}</td>
                  <td className="n">{pcs(totals.damaged)}</td>
                  <td className="n">{pcs(totals.sold)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </Box>
        <Typography className="sub" sx={{ lineHeight: 1.5 }}>
          เกิน = ยกมา + โรงงาน − 7-11 สั่ง + รับโอน − โอนออก · ติดลบ = ขาด · รับโอน/โอนออกต้องใส่หมายเหตุ (เช่น
          "ชำรุดโอนกลับโรงงาน") · ระบบเตือนถ้า เหลือ + ชำรุด + ขาย ≠ เกิน · "เหลือ" ของวันนี้เป็น "ยกมา" ของพรุ่งนี้
          {editable && " · ล้างช่องยกมาเพื่อกลับไปใช้ค่าระบบ"}
        </Typography>
      </Card>

      <Stack direction="row" gap={1.75} flexWrap="wrap" alignItems="flex-start">
        <Card sx={{ ...SECTION_SX, flex: "2 1 640px", minWidth: 0 }}>
          <Typography fontWeight={600}>ต่อคลัง</Typography>
          <Typography className="sub">
            ระบบเติมจากไฟล์ของวันนี้{editable ? " · แก้จำนวนร้านได้ถ้าไม่ตรงกับแบบฟอร์ม (ล้างช่อง = ใช้ค่าระบบ)" : ""}
          </Typography>
          <Box sx={{ overflowX: "auto" }}>
            <table className="bdc-t">
              <thead>
                <tr>
                  <th>DC</th>
                  <th className="n">ชิ้นที่สั่ง</th>
                  <th className="n">ร้านทั้งหมด</th>
                  <th className="n">ร้านที่สั่งวันนี้</th>
                  <th className="n">% ร้านที่สั่ง</th>
                </tr>
              </thead>
              <tbody>
                {day.dcs.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="c sub">
                      ยังไม่มี DC ผูกกับสาขานี้
                    </td>
                  </tr>
                ) : (
                  day.dcs.map((dc, index) => (
                    <DcRow
                      key={dc.dc_code}
                      source={dc}
                      view={view.dcs[index]}
                      draft={draft}
                      errors={errors}
                      editable={editable}
                      onChange={onDcChange}
                    />
                  ))
                )}
              </tbody>
              {day.dcs.length > 0 && (
                <tfoot>
                  <tr>
                    <td>รวม</td>
                    <td className="n">{pcs(view.dcs.reduce((sum, dc) => sum + dc.ordered_qty, 0))}</td>
                    <td className="n">{pcs(totals.store_total)}</td>
                    <td className="n">{pcs(totals.store_ordered)}</td>
                    <td className="n">
                      {pctText(percentOf(totals.store_ordered, totals.store_total))}
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </Box>
        </Card>

        <Card sx={{ ...SECTION_SX, flex: "1 1 300px", minWidth: 0 }} data-testid="bdc-stock-accounting">
          <Typography fontWeight={600}>แคปให้บัญชี</Typography>
          <Stack direction="row" justifyContent="space-between" fontSize="0.8125rem">
            <span>รายได้ก่อน VAT</span>
            <span className="n">{baht(accounting.subtotal)}</span>
          </Stack>
          <Stack direction="row" justifyContent="space-between" fontSize="0.8125rem">
            <span>VAT {accounting.vat_rate}%</span>
            <span className="n">{baht(accounting.vat)}</span>
          </Stack>
          <Stack
            direction="row"
            justifyContent="space-between"
            fontWeight={600}
            sx={{ borderTop: "1px solid #E6E7EB", pt: 0.75 }}
          >
            <span>รวม</span>
            <span className="n">{baht(accounting.total)}</span>
          </Stack>
          <Typography className="sub">ยอดสั่ง × ราคาต่อชิ้นจาก PO · ไม่รวม CN</Typography>
          <Stack gap={0.75} sx={{ borderTop: "1px solid #E6E7EB", pt: 1.25, fontSize: "0.8125rem" }}>
            <Stack direction="row" justifyContent="space-between">
              <span>ผู้จัดทำ</span>
              <span>{day.prepared_by_name || <span className="sub">กรอกตอนปิดวัน</span>}</span>
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <span>ผู้ตรวจสอบ</span>
              <span>{day.checked_by_name || <span className="sub">กรอกตอนปิดวัน</span>}</span>
            </Stack>
          </Stack>
        </Card>
      </Stack>
    </Stack>
  );
}
